const DOCUMENT_IMPORT_SIZE_FAILURE = {
    SourceFileTooLarge: 'source-file',
    ArchiveTooLarge: 'archive',
    ArchiveEntryTooLarge: 'archive-entry',
    ExtractionLimitExceeded: 'extraction',
    document_import_source_file_too_large: 'source-file',
    document_import_archive_too_large: 'archive',
    document_import_archive_entry_too_large: 'archive-entry',
    document_import_extraction_budget_exceeded: 'extraction',
} as const;

type DocumentImportSizeFailureKind =
    (typeof DOCUMENT_IMPORT_SIZE_FAILURE)[keyof typeof DOCUMENT_IMPORT_SIZE_FAILURE];

export interface DocumentImportSizeFailureDetails {
    code: string | null | undefined;
    actualBytes?: number | null;
    allowedBytes?: number | null;
}

const DOCUMENT_IMPORT_ARCHIVE_FAILURE_MESSAGES = {
    document_import_archive_empty:
        'The ZIP archive does not contain any files.',
    document_import_archive_invalid:
        'The ZIP archive is corrupt, password-protected, or otherwise invalid.',
    document_import_archive_depth_exceeded:
        'The ZIP archive contains nested archives beyond the allowed depth.',
    document_import_archive_extraction_failed:
        'The ZIP archive could not be extracted.',
    document_import_file_count_exceeded:
        'The archive batch contains more files than allowed.',
    document_import_in_flight_budget_exceeded:
        'The ZIP archive could not be processed within the available extraction capacity.',
    document_import_metadata_too_long:
        'A file name or folder path inside the ZIP archive is too long.',
    document_import_unsupported_file_type:
        'A file inside the ZIP archive has an unsupported file type.',
} as const;

export function isDocumentImportSizeFailure(
    code: string | null | undefined
): boolean {
    return toSizeFailureKind(code) !== null;
}

export function toDocumentImportSizeFailureMessage({
    code,
    actualBytes,
    allowedBytes,
}: DocumentImportSizeFailureDetails): string | null {
    const kind = toSizeFailureKind(code);
    if (!kind) return null;

    const sizeDetails = toSizeDetails(actualBytes, allowedBytes);

    switch (kind) {
        case 'source-file':
            return `A source file is larger than the allowed size${sizeDetails}.`;
        case 'archive':
            return `A ZIP archive is larger than the allowed size${sizeDetails}.`;
        case 'archive-entry':
            return `A file inside a ZIP archive is larger than the allowed size${sizeDetails}.`;
        case 'extraction':
            return `The files extracted from a ZIP archive exceed the total allowed size${sizeDetails}.`;
    }
}

export function toDocumentImportFailureMessage(
    details: DocumentImportSizeFailureDetails
): string | null {
    const sizeFailureMessage = toDocumentImportSizeFailureMessage(details);
    if (sizeFailureMessage) {
        if (
            details.code === 'document_import_extraction_budget_exceeded' &&
            (details.actualBytes == null || details.allowedBytes == null)
        ) {
            return 'The ZIP archive exceeds the extraction size or entry-count budget.';
        }

        return sizeFailureMessage;
    }

    if (!details.code) return null;

    return (
        DOCUMENT_IMPORT_ARCHIVE_FAILURE_MESSAGES[
            details.code as keyof typeof DOCUMENT_IMPORT_ARCHIVE_FAILURE_MESSAGES
        ] ?? null
    );
}

function toSizeFailureKind(
    code: string | null | undefined
): DocumentImportSizeFailureKind | null {
    if (!code) return null;

    return (
        DOCUMENT_IMPORT_SIZE_FAILURE[
            code as keyof typeof DOCUMENT_IMPORT_SIZE_FAILURE
        ] ?? null
    );
}

function toSizeDetails(
    actualBytes: number | null | undefined,
    allowedBytes: number | null | undefined
): string {
    if (actualBytes == null || allowedBytes == null) return '';

    return ` (${formatDecimalBytes(actualBytes)} actual; ${formatDecimalBytes(allowedBytes)} allowed)`;
}

export function formatDecimalBytes(bytes: number): string {
    const units = ['bytes', 'KB', 'MB', 'GB', 'TB'] as const;
    let value = Math.max(0, bytes);
    let unitIndex = 0;

    while (value >= 1_000 && unitIndex < units.length - 1) {
        value /= 1_000;
        unitIndex += 1;
    }

    const maximumFractionDigits = value >= 10 ? 1 : 2;
    return `${new Intl.NumberFormat('en-AU', { maximumFractionDigits }).format(value)} ${units[unitIndex]}`;
}
